import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db, auth } from '../../../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import EducationalLoans from '../../../components/initiate Services/EducationalLoans';
import ForexRemittance from '../../../components/initiate Services/ForexRemittance';
import StudentAccommodation from '../../../components/initiate Services/StudentAccommedation';
import TravelInsurance from '../../../components/initiate Services/TravelInsurance';
import { useSubAgentAuth } from '../SubAgent/SubAgentAuthContext';
import SideNavbar from '../../../components/SideNavbar';

// redux
import { useAppDispatch, useAppSelector } from '../../../redux/store';

interface StudentInfo {
  name: string;
  email: string;
  phone: string;
}

interface VideoInfo {
  url: string;
  title: string;
}

interface Service {
  heading: string;
  student: StudentInfo;
  currentStatus: string;
  status: string;
  video: VideoInfo;
}

const ServicesAdmin = () => {

  const { currentSubAgent } = useSubAgentAuth();

  const isCollapsed = useAppSelector(state => state.sidebar.isCollapsed);

  const [studentName, setStudentName] = useState("Loading...");
  const [studentId, setStudentId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [activeTab, setActiveTab] = useState<'eduloan' | 'forex' | 'studentAcco' | 'travelInsu' | null>('eduloan');

  const { leadId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStudentData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser || !leadId) return;
        let companyId: string | undefined;
        // Get user document to get companyId
        if (currentSubAgent) {
          companyId = currentSubAgent.companyId;
        } else {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          companyId = userDoc.data()?.companyId;
        }
        if (!companyId) return;

        const companyDoc = await getDoc(doc(db, 'companies', companyId));
        if (companyDoc.exists()) {
          setCompanyName(companyDoc.data().name || "Unknown Company");
        }
        const leadDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}`));
        if (leadDoc.exists()) {
          const studentData = leadDoc.data();
          setStudentName(studentData.name || "Unknown Student");
          setStudentId(leadDoc.id || "");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setStudentName("Error loading student");
        setCompanyName("Error loading company");
      }
    };

    fetchStudentData();
  }, [leadId]);

  // Handle tab click
  const handleTabClick = (tabName: 'eduloan' | 'forex' | 'studentAcco' | 'travelInsu' | null): void => {
    setActiveTab(tabName);
  };

  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'eduloan':
        return <EducationalLoans write={true} />;
      case 'forex':
        return <ForexRemittance write={true} />;
      case 'studentAcco':
        return <StudentAccommodation />;
      case 'travelInsu':
        return <TravelInsurance />;
      default:
        return (
          <div className="p-8 text-center text-gray-500">
            <p>Select a service to get started</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-200">
      <div className="fixed top-16 left-0 h-full z-10">
        <SideNavbar />
      </div>
      <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300 w-[calc(100%-${isCollapsed ? '70px' : '290px'})]`}>
        <div className="bg-white p-6 rounded-3xl shadow-md">
          <div className="flex items-center gap-6 mb-8">
            <div className="flex items-center justify-center w-14 h-14 rounded-full" style={{ background: 'linear-gradient(180deg, #00A6D9 0%, #3483BE 100%)' }}>
              <img src={`${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-service-white.svg`} alt="Lead Icon" />
            </div>
            <h2 className="text-2xl font-medium text-black"> Services </h2>
          </div>
          <div className="p-4">
            {/* Service tabs - Using the design from the first cod */}
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-4 w-2/3">
                <div
                  className={`bg-gray-100 rounded-3xl p-6 flex flex-col items-center justify-center text-center cursor-pointer transition-all hover:bg-gray-200 ${activeTab === 'eduloan' ? 'bg-gray-200 ring-2 ring-blue-500' : ''}`}
                  onClick={() => handleTabClick('eduloan')}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/desktop/qlementine-icons.svg`}
                    alt="Educational Loans"
                    className="w-6 h-6 mb-2 object-contain"
                  />
                  <h4 className="text-gray-800 text-sm font-medium">Educational Loans</h4>
                </div>

                <div
                  className={`bg-gray-100 rounded-3xl p-6 flex flex-col items-center justify-center text-center cursor-pointer transition-all hover:bg-gray-200 ${activeTab === 'forex' ? 'bg-gray-200 ring-2 ring-blue-500' : ''}`}
                  // title="This feature is coming soon"
                  onClick={() => handleTabClick('forex')}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/desktop/rupees.svg`}
                    alt="Forex Remittance"
                    className="w-6 h-6 mb-2 object-contain"
                  />
                  <h4 className="text-gray-800 text-sm font-medium">Forex Remittance</h4>
                  {/* <span className="text-xs bg-gray-300 text-gray-600 px-2 py-0.5 rounded mt-1">Coming Soon</span> */}
                </div>

                <div
                  className="bg-gray-100 rounded-3xl p-6 flex flex-col items-center justify-center text-center cursor-not-allowed opacity-50 transition-all"
                  title="This feature is coming soon"
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/desktop/home-outline.svg`}
                    alt="Student Accommodation"
                    className="w-6 h-6 mb-2  object-contain"
                  />
                  <h4 className="text-gray-800 text-sm font-medium">Student Accommodation</h4>
                  <span className="text-xs bg-gray-300 text-gray-600 px-2 py-0.5 rounded mt-1">Coming Soon</span>
                </div>

                <div
                  className="bg-gray-100 rounded-3xl p-6 flex flex-col items-center justify-center text-center cursor-not-allowed opacity-50 transition-all"
                  title="This feature is coming soon"
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/desktop/airplane-outline.svg`}
                    alt="Travel Insurance"
                    className="w-6 h-6 mb-2 object-contain"
                  />
                  <h4 className="text-gray-800 text-sm font-medium">Travel Insurance</h4>
                  <span className="text-xs bg-gray-300 text-gray-600 px-2 py-0.5 rounded mt-1">Coming Soon</span>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-2 min-h-40">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesAdmin;